// @flow

import clsx from 'clsx';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import type { FormProps } from 'redux-form';
import * as url from 'url';

import { Button, Base } from '@gandi/ui';
import { Intro } from 'gandi.react-public-layout';

import { withPageMeta } from '@gandi/app-runner/dist/utils/withPageMeta';
import { notifyDanger } from '@gandi/notifications';
import { Form, withForm } from 'gandi.react-form';
import { useTranslator } from '@gandi/translate';

import { compose } from 'redux';

import { withRouter, type WithRouterProps } from 'src/helpers/withRouter';

import Layout from '../../containers/Layout';
import styles from '../Unsubscriptions.scss';

import {
  deleteNotificationSubcribersCompaign,
  submitUnsubscriptionComplaignForm,
} from '../modules';
import type { HandleForm } from '../../types';

type InjectedProps = {|
  ...WithRouterProps<{|
    campaignSlug: 'kit' | string,
    token: string,
  |}>,
  reduxFormProps: FormProps,
|};
export type Props = {|
  ...InjectedProps,
|};

function Campaigns({
  location,
  params: { campaignSlug, token },
  reduxFormProps: { handleSubmit, submitFailed },
  router,
}: Props): React.Element<typeof Layout> {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (submitFailed) {
      const message = __(
        'A temporary error occured while sending your confirmation.'
      );
      dispatch(notifyDanger(message));
    }
  }, [__, dispatch, submitFailed]);

  const handleForm = () => {
    dispatch(deleteNotificationSubcribersCompaign(token, campaignSlug))
      .then(() =>
        router.replace(`/unsubscription/${campaignSlug}/${token}/success`)
      )
      .catch(err => {
        const message =
          err.response.data.message ||
          __('A temporary error occured while sending your confirmation.');
        dispatch(
          notifyDanger({ message: <Base overflow="break">{message}</Base> })
        );
      });
  };

  return (
    <Layout location={location}>
      <Base className={clsx('wrapper-sm', styles.center)}>
        <Intro title={__('Please confirm you want to unsubscribe')} />
        <Form method="post" onSubmit={handleSubmit(handleForm)}>
          <Base backgroundColor="white" padding="small" marginBottom="base">
            <Base tagName="p" margin="none">
              {__(
                "You received an email from Gandi about products you purchased. Please confirm you don't want to receive these emails anymore."
              )}
            </Base>
          </Base>

          <Button type="submit">{__('Unsubscribe')}</Button>
        </Form>
      </Base>
    </Layout>
  );
}

const handleForm = ({
  dispatch,
  req,
  res,
  params,
  translator: { __ },
}: HandleForm<Required<InjectedProps>>) => {
  // $FlowFixMe[incompatible-type] mixed body
  const data: {| unsubscription_choice?: string |} = req.body || {};

  const currentUrl = url.format({
    protocol: req.protocol,
    hostname: req.hostname,
    pathname: req.path,
    query: req.query,
  });

  return dispatch(
    // $FlowFixMe[incompatible-call] unsubscription_choice can be missing
    submitUnsubscriptionComplaignForm(data, params, __, req, res, currentUrl)
  ); // send data to the API and redirect
};

export default (compose(
  // $FlowFixMe[underconstrained-implicit-instantiation]
  withRouter,
  withPageMeta(({ __ }) => ({
    title: __('Unsubscription - Gandi.net'),
    robots: 'noindex',
  })),
  withForm({
    form: 'UnsubscriptionCampaignForm',
    propNamespace: 'reduxFormProps',
    serverHandler: handleForm,
  })
)(Campaigns): React.ComponentType<{||}>);
